/**
 * Observabilidade do front — Sentry.
 *
 * Sem `VITE_SENTRY_DSN` no build, tudo aqui vira no-op: dev local e preview
 * não mandam nada, e ninguém precisa de mock pra rodar.
 *
 * Decisões que o plano gratuito e o produto impõem:
 *
 *   • Sem Session Replay e sem profiling. Replay é o item que mais consome
 *     cota, e num app que mostra dados de cliente ele também seria uma
 *     gravação de tela com telemetria de terceiro dentro.
 *   • `sendDefaultPii: false` — nada de e-mail, IP ou cabeçalho de auth.
 *   • Erro vindo de extensão do navegador é descartado: é ruído do
 *     ambiente do usuário, não defeito nosso, e come cota rápido.
 *   • Trace amostrado baixo, e a rota /health nunca entra.
 */

import * as Sentry from "@sentry/react";

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

/** Origens que não são o nosso código rodando. */
const RUIDO_DE_EXTENSAO = [
  /^chrome-extension:\/\//,
  /^moz-extension:\/\//,
  /^safari-web-extension:\/\//,
];

/** Mensagens que só aparecem por rede instável ou aba fechada no meio. */
const RUIDO_DE_REDE = [
  "Failed to fetch",
  "NetworkError when attempting to fetch resource",
  "Load failed",
  "AbortError",
  "ResizeObserver loop",
];

export function initObs(): boolean {
  if (!DSN) return false;

  Sentry.init({
    dsn: DSN,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_RELEASE as string | undefined,
    sendDefaultPii: false,
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_RATE ?? 0.05),
    integrations: [Sentry.browserTracingIntegration()],
    denyUrls: RUIDO_DE_EXTENSAO,
    ignoreErrors: RUIDO_DE_REDE,
    beforeSend(evento) {
      // O usuário é identificado por id, nunca por e-mail ou IP: o painel
      // mostra dados de cliente e o time de suporte não precisa do resto.
      if (evento.user) {
        evento.user = { id: evento.user.id };
      }
      return evento;
    },
  });
  return true;
}

/** Marca quem está na sessão — só o id, ver `beforeSend`. */
export function identificar(userId: string | null): void {
  if (!DSN) return;
  Sentry.setUser(userId ? { id: userId } : null);
}
